import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { fetchRoleBasedProjects, PROJECT_LIST_COLUMNS } from '@/lib/fetchRoleBasedProjects';
import { primeSidebarProjectsCache } from '@/lib/sidebarProjectsCache';

export interface ManagementProjectLite {
  id: string;
  project_name: string;
  project_code: string;
  client_name: string | null;
  site_address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  status: string;
  summary: string | null;
  start_date: string | null;
  target_completion_date: string | null;
  actual_completion_date: string | null;
  project_type: string | null;
  cover_image: string | null;
  cover_photo_url: string | null;
  client_user_id: string | null;
  project_progress: number | null;
  schedule_progress: number | null;
  checklist_progress: number | null;
  pending_schedule_items: number | null;
  current_stage: string | null;
  created_at: string;
  updated_at: string;
  calculatedProgress: number;
  calculatedStatus: 'active' | 'planning' | 'on_hold' | 'complete';
  calculatedStage: string;
  scheduleFinalPct: number;
  checklistFinalPct: number;
}

export interface UseManagementProjectsLiteResult {
  projects: ManagementProjectLite[];
  loading: boolean;
  error: string | null;
  refreshing: boolean;
  refresh: () => void;
}

function normalizeStatus(raw: string | null): ManagementProjectLite['calculatedStatus'] {
  const s = (raw || '').toLowerCase().trim().replace(/[\s_-]+/g, '_');
  if (s === 'on_hold' || s === 'onhold' || s === 'hold' || s === 'paused' || s === 'suspended') return 'on_hold';
  if (s === 'completed' || s === 'complete' || s === 'done' || s === 'finished' || s === 'closed') return 'complete';
  if (s === 'active' || s === 'in_progress' || s === 'inprogress' || s === 'ongoing' || s === 'started' || s === 'progress' || s === 'construction') return 'active';
  if (s === 'planning' || s === 'planned' || s === 'draft' || s === 'new' || s === 'preparation' || s === 'preparing') return 'planning';
  if (raw) return 'active';
  return 'planning';
}

function mapRow(row: Record<string, unknown>): ManagementProjectLite {
  const status = normalizeStatus(row.status as string | null);
  const progress = (row.project_progress as number | null) ?? 0;
  const stage = (row.current_stage as string | null) || 'Planning';
  const schedulePct = (row.schedule_progress as number | null) ?? 0;
  const checklistPct = (row.checklist_progress as number | null) ?? 0;

  return {
    id: String(row.id),
    project_name: String(row.project_name || 'Untitled Project'),
    project_code: (row.project_code as string | null) || String(row.id).slice(0, 6).toUpperCase(),
    client_name: (row.client_name as string | null) || null,
    site_address: (row.site_address as string | null) || null,
    city: (row.city as string | null) || null,
    state: (row.state as string | null) || null,
    country: (row.country as string | null) || null,
    status: String(row.status || 'planning'),
    summary: (row.summary as string | null) || null,
    start_date: (row.start_date as string | null) || null,
    target_completion_date: (row.target_completion_date as string | null) || null,
    actual_completion_date: (row.actual_completion_date as string | null) || null,
    project_type: (row.project_type as string | null) || 'Residential',
    cover_image: (row.cover_image as string | null) || null,
    cover_photo_url: (row.cover_photo_url as string | null) || null,
    client_user_id: (row.client_user_id as string | null) || null,
    project_progress: (row.project_progress as number | null) ?? null,
    schedule_progress: (row.schedule_progress as number | null) ?? null,
    checklist_progress: (row.checklist_progress as number | null) ?? null,
    pending_schedule_items: (row.pending_schedule_items as number | null) ?? null,
    current_stage: (row.current_stage as string | null) || null,
    created_at: String(row.created_at || new Date().toISOString()),
    updated_at: String(row.updated_at || new Date().toISOString()),
    calculatedProgress: progress,
    calculatedStatus: status,
    calculatedStage: progress === 100 ? 'Completed' : stage,
    scheduleFinalPct: schedulePct,
    checklistFinalPct: checklistPct,
  };
}

export function useManagementProjectsLite(): UseManagementProjectsLiteResult {
  const { user, profile, loading: authLoading } = useAuth();
  const [projects, setProjects] = useState<ManagementProjectLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const isMountedRef = useRef(true);
  const fetchGenRef = useRef(0);

  const load = useCallback(async (isRefresh = false) => {
    const gen = ++fetchGenRef.current;

    if (!isRefresh) {
      setLoading(true);
    }
    setRefreshing(isRefresh);
    setError(null);

    const profileId = profile?.id ?? user?.id ?? null;
    const role = profile?.user_type?.trim().toLowerCase() ?? null;

    try {
      const result = await fetchRoleBasedProjects({
        columns: PROJECT_LIST_COLUMNS,
        profileId: profileId ?? undefined,
        role: role ?? undefined,
      });

      if (!isMountedRef.current || fetchGenRef.current !== gen) {
        return;
      }

      if (result.error) {
        console.error('[useManagementProjectsLite] load failed', {
          table: 'jphph_projects',
          error: result.error,
          role: result.role ?? role,
          profileId: result.profileId ?? profileId,
        });
        setError(result.error);
        setProjects([]);
        return;
      }

      const mapped = result.rows.map(mapRow);
      console.log('[useManagementProjectsLite] loaded projects', {
        count: mapped.length,
        role: result.role ?? role,
        profileId: result.profileId ?? profileId,
      });
      setProjects(mapped);
      const cacheProfileId = result.profileId ?? profileId;
      const cacheRole = result.role ?? role;
      if (cacheProfileId && cacheRole) {
        primeSidebarProjectsCache(
          cacheProfileId,
          cacheRole,
          mapped.map((p) => ({ id: p.id, project_name: p.project_name }))
        );
      }
    } catch (err) {
      if (!isMountedRef.current || fetchGenRef.current !== gen) {
        return;
      }
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[useManagementProjectsLite] unexpected error', {
        message: msg,
        role,
        profileId,
      });
      setError(msg || 'Unexpected error loading projects');
      setProjects([]);
    } finally {
      if (isMountedRef.current && fetchGenRef.current === gen) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [profile?.id, profile?.user_type, user?.id]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!user?.id) {
      if (!authLoading) {
        setProjects([]);
        setError('Not authenticated');
        setLoading(false);
        setRefreshing(false);
      }
      return;
    }

    load(false);
  }, [authLoading, user?.id, load]);

  const refresh = useCallback(() => {
    if (!user?.id) {
      return;
    }
    load(true);
  }, [user?.id, load]);

  return { projects, loading, error, refreshing, refresh };
}
